import { useContext, useMemo } from "react";
import type { ReactNode } from "react";
import { Button } from "antd";
import {
  ApartmentOutlined,
  AppstoreOutlined,
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  CalculatorOutlined,
  CarOutlined,
  CarryOutOutlined,
  CodeSandboxOutlined,
  ProjectOutlined,
  RiseOutlined,
  ShoppingCartOutlined,
  SlidersOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import QLayoutContext from "contexts/Layout";
import AppSearch from "components/layout/AppSearch";
import { resolveLabel } from "components/layout/SideMenu";
import styles from "styles/qerp_landing.scss";
import { buildLandingTiles } from "./tiles";

export interface QerpLandingProps {
  planName?: string | null;
  dashboardPath: string;
}

// Large glyphs for the tiles; the sidebar's 16px icons repeat (two file icons) at this size.
const TILE_ICONS: Record<string, ReactNode> = {
  sidepanel_sales: <RiseOutlined />,
  sidepanel_delivery_orders: <CarOutlined />,
  sidepanel_purchases: <ShoppingCartOutlined />,
  sidepanel_purchase_requests: <CarryOutOutlined />,
  sidepanel_expense_claims: <AuditOutlined />,
  sidepanel_products: <CodeSandboxOutlined />,
  sidepanel_fixed_assets: <CalculatorOutlined />,
  sidepanel_accounting: <BookOutlined />,
  sidepanel_projects_tasks: <ProjectOutlined />,
  sidepanel_reports: <BarChartOutlined />,
  sidepanel_settings__workflow_board: <ApartmentOutlined />,
  sidepanel_settings: <SlidersOutlined />,
};

const greetingKey = (hour: number) => {
  if (hour < 12) return "good_morning";
  if (hour < 17) return "good_afternoon";
  return "good_evening";
};

const QerpLanding = ({ planName, dashboardPath }: QerpLandingProps) => {
  const { t } = useTranslation();
  const { sideMenuData, currentUser, organizationName } = useContext(QLayoutContext);
  const tiles = useMemo(() => buildLandingTiles(sideMenuData), [sideMenuData]);
  // Rails-style placeholder; window.I18n strings are not run through i18next interpolation.
  const greeting = t(`qerp.landing.${greetingKey(new Date().getHours())}`).replace(
    "%{name}",
    currentUser.label ?? "",
  );

  return (
    <main className={styles.qerp_landing}>
      <div className={styles.qerp_landing__inner}>
        <header className={styles.qerp_landing__head}>
          <div>
            <h1 className={styles.qerp_landing__greeting}>{greeting}</h1>
            <p className={styles.qerp_landing__subtitle}>
              {[organizationName, planName].filter(Boolean).join(" · ")}
            </p>
          </div>
          <div className={styles.qerp_landing__actions}>
            <div className={styles.qerp_landing__search}>
              <AppSearch />
            </div>
            <Button
              size="large"
              icon={<AppstoreOutlined />}
              href={dashboardPath}
              className={styles.qerp_landing__all_button}
            >
              {t("qerp.landing.all_modules")}
            </Button>
          </div>
        </header>

        <ul className={styles.qerp_landing__grid}>
          {tiles.map((tile) => (
            <li key={tile.id}>
              <a className={styles.qerp_landing__tile} href={tile.href}>
                <span className={styles.qerp_landing__icon} style={{ background: tile.color }}>
                  {TILE_ICONS[tile.id]}
                </span>
                <span className={styles.qerp_landing__label}>{t(resolveLabel(tile.id))}</span>
              </a>
            </li>
          ))}
          <li>
            <a className={styles.qerp_landing__tile} href={dashboardPath}>
              <span className={`${styles.qerp_landing__icon} ${styles.qerp_landing__icon_all}`}>
                <AppstoreOutlined />
              </span>
              <span className={styles.qerp_landing__label}>{t("qerp.landing.all_modules")}</span>
            </a>
          </li>
        </ul>

        <p className={styles.qerp_landing__hint}>{t("qerp.landing.hint")}</p>
      </div>
    </main>
  );
};

export default QerpLanding;
